import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgRole } from '@finvox/shared/org';
import {
  CASHBOOK_EXPORT_TYPES,
  CASHBOOK_VIEW_MODES,
  cashbookOverridesFromAccess,
  type CashbookAccess,
  type CashbookExportPermission,
  type CashbookViewModePermission
} from '@finvox/shared/cashbook-access';
import {
  defaultMemberAccessForRole,
  hasMemberPermissionOverrides,
  MODULE_IDS,
  type MemberPermissionOverrides,
  resolveMemberAccess,
  SETTINGS_SECTION_IDS,
  SIDEBAR_MODULE_IDS
} from '@finvox/shared/member-access';
import { Button } from '@/components/ui/Button';

const MODULE_LABEL_KEYS: Record<(typeof SIDEBAR_MODULE_IDS)[number], string> = {
  dashboard: 'nav.dashboard',
  coa: 'nav.coa',
  journals: 'nav.journals',
  sales: 'nav.salesAr',
  purchases: 'nav.purchasesAp',
  inventory: 'nav.inventory',
  banking: 'nav.banking',
  taxes: 'nav.taxes',
  reports: 'nav.reports',
  import: 'nav.importIntegrations',
  fixedAssets: 'nav.fixedAssets',
  payroll: 'nav.payroll',
  settings: 'nav.settings'
};

const SETTINGS_LABEL_KEYS: Record<(typeof SETTINGS_SECTION_IDS)[number], string> = {
  organization: 'settings.organization',
  branding: 'settings.branding',
  language: 'settings.language',
  regional: 'settings.regional',
  users: 'settings.users',
  costCenters: 'settings.costCenters',
  security: 'settings.security',
  eInvoicing: 'settings.eInvoicing',
  reportSchedule: 'settings.reportSchedule',
  budget: 'settings.budget',
  taxGl: 'settings.taxGl',
  fiscalPeriod: 'settings.fiscalPeriod',
  auditLog: 'settings.auditLog'
};

const VIEW_MODE_LABEL_KEYS: Record<CashbookViewModePermission, string> = {
  day: 'cashbook.viewDay',
  month: 'cashbook.viewMonth',
  year: 'cashbook.viewYear',
  multi: 'cashbook.viewMulti'
};

const EXPORT_LABEL_KEYS: Record<CashbookExportPermission, string> = {
  csv: 'cashbook.exportCsv',
  xlsx: 'cashbook.exportExcel',
  pdf: 'cashbook.exportPdf'
};

function buildMemberPermissionsPayload(
  role: OrgRole,
  modules: Record<string, boolean>,
  settings: Record<string, boolean>,
  cashbook: CashbookAccess
): MemberPermissionOverrides {
  const defaults = defaultMemberAccessForRole(role);
  const out: MemberPermissionOverrides = { modules: {}, settings: {}, cashbook: {} };
  for (const id of MODULE_IDS) {
    if (modules[id] !== defaults.modules[id]) out.modules![id] = modules[id];
  }
  for (const id of SETTINGS_SECTION_IDS) {
    if (settings[id] !== defaults.settings[id]) out.settings![id] = settings[id];
  }
  const cashbookOverrides = cashbookOverridesFromAccess(role, cashbook);
  if (Object.keys(cashbookOverrides).length) out.cashbook = cashbookOverrides;

  const payload: MemberPermissionOverrides = {};
  if (out.modules && Object.keys(out.modules).length) payload.modules = out.modules;
  if (out.settings && Object.keys(out.settings).length) payload.settings = out.settings;
  if (out.cashbook && Object.keys(out.cashbook).length) payload.cashbook = out.cashbook;
  return payload;
}

function cashbookDirty(a: CashbookAccess, b: CashbookAccess): boolean {
  if (a.post !== b.post || a.import !== b.import || a.void !== b.void) return true;
  if (
    a.analytics !== b.analytics ||
    a.debts !== b.debts ||
    a.datePicker !== b.datePicker ||
    a.balanceColumn !== b.balanceColumn
  ) {
    return true;
  }
  return (
    CASHBOOK_VIEW_MODES.some((mode) => a.viewModes[mode] !== b.viewModes[mode]) ||
    CASHBOOK_EXPORT_TYPES.some((kind) => a.exports[kind] !== b.exports[kind])
  );
}

function cashbookAllEnabled(cashbook: CashbookAccess): boolean {
  return (
    cashbook.post &&
    cashbook.import &&
    cashbook.void &&
    cashbook.analytics &&
    cashbook.debts &&
    cashbook.datePicker &&
    cashbook.balanceColumn &&
    CASHBOOK_VIEW_MODES.every((mode) => cashbook.viewModes[mode]) &&
    CASHBOOK_EXPORT_TYPES.every((kind) => cashbook.exports[kind])
  );
}

function cashbookAllDisabled(cashbook: CashbookAccess): boolean {
  return (
    !cashbook.post &&
    !cashbook.import &&
    !cashbook.void &&
    !cashbook.analytics &&
    !cashbook.debts &&
    !cashbook.datePicker &&
    !cashbook.balanceColumn &&
    CASHBOOK_VIEW_MODES.every((mode) => !cashbook.viewModes[mode]) &&
    CASHBOOK_EXPORT_TYPES.every((kind) => !cashbook.exports[kind])
  );
}

function buildCashbookAccess(enabled: boolean): CashbookAccess {
  return {
    post: enabled,
    import: enabled,
    void: enabled,
    analytics: enabled,
    debts: enabled,
    datePicker: enabled,
    balanceColumn: enabled,
    viewModes: Object.fromEntries(CASHBOOK_VIEW_MODES.map((mode) => [mode, enabled])) as Record<
      CashbookViewModePermission,
      boolean
    >,
    exports: Object.fromEntries(CASHBOOK_EXPORT_TYPES.map((kind) => [kind, enabled])) as Record<
      CashbookExportPermission,
      boolean
    >
  };
}

function buildAllModules(enabled: boolean): Record<(typeof MODULE_IDS)[number], boolean> {
  return Object.fromEntries(MODULE_IDS.map((id) => [id, enabled])) as Record<
    (typeof MODULE_IDS)[number],
    boolean
  >;
}

function buildAllSettings(enabled: boolean): Record<(typeof SETTINGS_SECTION_IDS)[number], boolean> {
  return Object.fromEntries(SETTINGS_SECTION_IDS.map((id) => [id, enabled])) as Record<
    (typeof SETTINGS_SECTION_IDS)[number],
    boolean
  >;
}

export function MemberPermissionsEditor({
  role,
  permissions,
  legacyCashbook,
  online,
  busy,
  variant = 'member',
  onChange,
  onSave,
  onReset
}: {
  role: OrgRole;
  permissions?: MemberPermissionOverrides;
  legacyCashbook?: MemberPermissionOverrides['cashbook'];
  online: boolean;
  busy: boolean;
  variant?: 'member' | 'draft';
  onChange?: (permissions: MemberPermissionOverrides) => void;
  onSave?: (permissions: MemberPermissionOverrides) => Promise<void>;
  onReset?: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const effective = useMemo(
    () => resolveMemberAccess(role, permissions, legacyCashbook),
    [role, permissions, legacyCashbook]
  );
  const [modules, setModules] = useState(effective.modules);
  const [settings, setSettings] = useState(effective.settings);
  const [cashbook, setCashbook] = useState(effective.cashbook);
  const customized = hasMemberPermissionOverrides(permissions, legacyCashbook);
  const cashbookAccessOn = modules.cashbook;

  useEffect(() => {
    const next = resolveMemberAccess(role, permissions, legacyCashbook);
    setModules(next.modules);
    setSettings(next.settings);
    setCashbook(next.cashbook);
  }, [role, permissions, legacyCashbook]);

  const dirty = useMemo(() => {
    return (
      MODULE_IDS.some((id) => modules[id] !== effective.modules[id]) ||
      SETTINGS_SECTION_IDS.some((id) => settings[id] !== effective.settings[id]) ||
      cashbookDirty(cashbook, effective.cashbook)
    );
  }, [modules, settings, cashbook, effective]);

  useEffect(() => {
    if (variant !== 'draft' || !onChange) return;
    onChange(buildMemberPermissionsPayload(role, modules, settings, cashbook));
  }, [variant, onChange, role, modules, settings, cashbook]);

  const allChecked = useMemo(
    () =>
      MODULE_IDS.every((id) => modules[id]) &&
      SETTINGS_SECTION_IDS.every((id) => settings[id]) &&
      modules.cashbook &&
      cashbookAllEnabled(cashbook),
    [modules, settings, cashbook]
  );

  const noneChecked = useMemo(
    () =>
      MODULE_IDS.every((id) => !modules[id]) &&
      SETTINGS_SECTION_IDS.every((id) => !settings[id]) &&
      !modules.cashbook &&
      cashbookAllDisabled(cashbook),
    [modules, settings, cashbook]
  );

  function setAllPermissions(enabled: boolean) {
    setModules(buildAllModules(enabled));
    setSettings(buildAllSettings(enabled));
    setCashbook(buildCashbookAccess(enabled));
  }

  const shellClass =
    variant === 'draft'
      ? 'text-xs'
      : 'mt-2 w-full rounded border border-[var(--fv-border)] bg-[var(--fv-bg)]/50 p-3 text-xs';

  return (
    <div className={shellClass}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-[var(--fv-text)]">{t('settings.memberPermissionsTitle')}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <label className="flex items-center gap-2 text-[var(--fv-text-muted)]">
            <input
              type="checkbox"
              checked={allChecked}
              disabled={!online || busy}
              onChange={(e) => {
                if (e.target.checked) setAllPermissions(true);
              }}
            />
            <span>{t('settings.permCheckAll')}</span>
          </label>
          <label className="flex items-center gap-2 text-[var(--fv-text-muted)]">
            <input
              type="checkbox"
              checked={noneChecked}
              disabled={!online || busy}
              onChange={(e) => {
                if (e.target.checked) setAllPermissions(false);
              }}
            />
            <span>{t('settings.permUncheckAll')}</span>
          </label>
        </div>
      </div>

      <div className="mt-3">
        <p className="mb-1 font-medium text-[var(--fv-text-muted)]">{t('settings.cashbookPermissionsTitle')}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={modules.cashbook}
              disabled={!online || busy}
              onChange={(e) => setModules((prev) => ({ ...prev, cashbook: e.target.checked }))}
            />
            <span>{t('settings.cashbookPermAccess')}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cashbook.post}
              disabled={!online || busy || !cashbookAccessOn}
              onChange={(e) => setCashbook((prev) => ({ ...prev, post: e.target.checked }))}
            />
            <span>{t('settings.cashbookPermPost')}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cashbook.import}
              disabled={!online || busy || !cashbookAccessOn}
              onChange={(e) => setCashbook((prev) => ({ ...prev, import: e.target.checked }))}
            />
            <span>{t('settings.cashbookPermImport')}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cashbook.void}
              disabled={!online || busy || !cashbookAccessOn}
              onChange={(e) => setCashbook((prev) => ({ ...prev, void: e.target.checked }))}
            />
            <span>{t('settings.cashbookPermVoid')}</span>
          </label>
        </div>

        <p className="mb-1 mt-3 font-medium text-[var(--fv-text-muted)]">
          {t('settings.cashbookPermUiTitle')}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cashbook.analytics}
              disabled={!online || busy || !cashbookAccessOn}
              onChange={(e) => setCashbook((prev) => ({ ...prev, analytics: e.target.checked }))}
            />
            <span>{t('settings.cashbookPermAnalytics')}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cashbook.debts}
              disabled={!online || busy || !cashbookAccessOn}
              onChange={(e) => setCashbook((prev) => ({ ...prev, debts: e.target.checked }))}
            />
            <span>{t('settings.cashbookPermDebts')}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cashbook.datePicker}
              disabled={!online || busy || !cashbookAccessOn}
              onChange={(e) => setCashbook((prev) => ({ ...prev, datePicker: e.target.checked }))}
            />
            <span>{t('settings.cashbookPermDatePicker')}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cashbook.balanceColumn}
              disabled={!online || busy || !cashbookAccessOn}
              onChange={(e) => setCashbook((prev) => ({ ...prev, balanceColumn: e.target.checked }))}
            />
            <span>{t('settings.cashbookPermBalanceColumn')}</span>
          </label>
        </div>

        <p className="mb-1 mt-3 font-medium text-[var(--fv-text-muted)]">
          {t('settings.cashbookPermViewModesTitle')}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {CASHBOOK_VIEW_MODES.map((mode) => (
            <label key={mode} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={cashbook.viewModes[mode]}
                disabled={!online || busy || !cashbookAccessOn}
                onChange={(e) =>
                  setCashbook((prev) => ({
                    ...prev,
                    viewModes: { ...prev.viewModes, [mode]: e.target.checked }
                  }))
                }
              />
              <span>{t(VIEW_MODE_LABEL_KEYS[mode])}</span>
            </label>
          ))}
        </div>

        <p className="mb-1 mt-3 font-medium text-[var(--fv-text-muted)]">
          {t('settings.cashbookPermExportsTitle')}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {CASHBOOK_EXPORT_TYPES.map((kind) => (
            <label key={kind} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={cashbook.exports[kind]}
                disabled={!online || busy || !cashbookAccessOn}
                onChange={(e) =>
                  setCashbook((prev) => ({
                    ...prev,
                    exports: { ...prev.exports, [kind]: e.target.checked }
                  }))
                }
              />
              <span>{t(EXPORT_LABEL_KEYS[kind])}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mt-3">
        <p className="mb-1 font-medium text-[var(--fv-text-muted)]">{t('settings.permModulesTitle')}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {SIDEBAR_MODULE_IDS.map((id) => (
            <label key={id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={modules[id]}
                disabled={!online || busy}
                onChange={(e) => setModules((prev) => ({ ...prev, [id]: e.target.checked }))}
              />
              <span>{t(MODULE_LABEL_KEYS[id])}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mt-3">
        <p className="mb-1 font-medium text-[var(--fv-text-muted)]">{t('settings.permSettingsTitle')}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {SETTINGS_SECTION_IDS.map((id) => (
            <label key={id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings[id]}
                disabled={!online || busy}
                onChange={(e) => setSettings((prev) => ({ ...prev, [id]: e.target.checked }))}
              />
              <span>{t(SETTINGS_LABEL_KEYS[id])}</span>
            </label>
          ))}
        </div>
      </div>

      {variant === 'member' ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!online || busy || !dirty}
            onClick={() => void onSave?.(buildMemberPermissionsPayload(role, modules, settings, cashbook))}
          >
            {t('common.save')}
          </Button>
          {customized ? (
            <Button variant="link" size="sm" disabled={!online || busy} onClick={() => void onReset?.()}>
              {t('settings.resetMemberPermissions')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export { MODULE_IDS, SETTINGS_SECTION_IDS, SIDEBAR_MODULE_IDS, buildMemberPermissionsPayload };
